import AvatarImage from '../components/AvatarImage';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, AuthContext } from '../App';
import { ArrowLeft, Users, UserPlus, Ban, Check, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const UserFollowers = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useContext(AuthContext);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  // followStatusMap = { [userId]: { following: bool, requested: bool } }
  const [followStatusMap, setFollowStatusMap] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadUserAndFollowers();
  }, [username]);

  const loadUserAndFollowers = async () => {
    try {
      setLoading(true);
      // First get the user's profile to get their ID
      const profileRes = await apiClient.get(`/public/users/${username}/profile`);
      const profile = profileRes.data;
      setUserId(profile.id);
      
      // Check if we can view social graph
      if (!profile.can_view_social_graph) {
        toast.error('Cannot view this user\'s followers');
        navigate(`/player/${username}`);
        return;
      }
      
      // Now get their followers
      const followersRes = await apiClient.get(`/users/${profile.id}/followers`);
      const followersList = followersRes.data.followers || [];
      setFollowers(followersList);
      
      // Build follow status map if logged in using bulk endpoint
      if (authUser?.id && followersList.length > 0) {
        const followerIds = followersList.map(f => f.id).filter(id => id !== authUser.id);
        if (followerIds.length > 0) {
          try {
            const statusRes = await apiClient.get(`/follow/status-bulk?user_ids=${followerIds.join(',')}`);
            setFollowStatusMap(statusRes.data.statuses || {});
          } catch {
            setFollowStatusMap({});
          }
        }
      }
    } catch (error) {
      console.error('Failed to load followers', error);
      if (error.response?.status === 403) {
        toast.error('Cannot view this user\'s followers');
        navigate(`/player/${username}`);
      } else {
        toast.error('Failed to load followers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (targetId, targetUsername) => {
    if (!authUser) {
      toast.error('Please log in to follow users');
      return;
    }
    
    const currentStatus = followStatusMap[targetId] || { following: false, requested: false };
    
    setActionLoading(prev => ({ ...prev, [targetId]: 'follow' }));
    try {
      if (currentStatus.following || currentStatus.requested) {
        // Unfollow or cancel request
        await apiClient.delete(`/follow/${targetId}`);
        setFollowStatusMap(prev => ({ 
          ...prev, 
          [targetId]: { following: false, requested: false } 
        }));
        toast.success(currentStatus.following ? 'Unfollowed' : 'Request cancelled');
      } else {
        // Follow - check response for actual state
        const response = await apiClient.post(`/follow/${targetId}`);
        const data = response.data;
        
        if (data.following) {
          setFollowStatusMap(prev => ({ 
            ...prev, 
            [targetId]: { following: true, requested: false } 
          }));
          toast.success(`Following ${targetUsername}!`);
        } else if (data.request_pending) {
          setFollowStatusMap(prev => ({ 
            ...prev, 
            [targetId]: { following: false, requested: true } 
          }));
          toast.success(`Follow request sent to ${targetUsername}`);
        }
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setActionLoading(prev => ({ ...prev, [targetId]: null }));
    }
  };

  const handleBlock = async (targetId, targetUsername) => {
    if (!authUser) {
      toast.error('Please log in to block users');
      return;
    }
    
    if (!window.confirm(`Block ${targetUsername}? They won't be able to see your profile or join your games.`)) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [targetId]: 'block' }));
    try {
      await apiClient.post(`/users/${targetId}/block`);
      toast.success(`Blocked ${targetUsername}`);
      // Remove from list
      setFollowers(prev => prev.filter(f => f.id !== targetId));
    } catch (error) {
      toast.error('Failed to block user');
    } finally {
      setActionLoading(prev => ({ ...prev, [targetId]: null }));
    }
  };

  // Skill badge styling
  const getSkillBadgeStyle = (skill) => {
    const skillLower = (skill || '').toLowerCase();
    if (skillLower.includes('elite') || skillLower.includes('advanced')) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    } else if (skillLower.includes('intermediate')) {
      return 'bg-blue-50 text-blue-700 border-blue-200';
    } else if (skillLower.includes('beginner')) {
      return 'bg-green-50 text-green-700 border-green-200';
    }
    return 'bg-zinc-50 text-zinc-600 border-zinc-200';
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div
        className="bg-white border-b border-zinc-100 sticky top-0 z-10"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-lg mx-auto px-4 pb-4 flex items-center gap-3">
          <button
            onClick={() => navigate(`/player/${username}`)}
            className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-zinc-900">Followers</h1>
            <p className="text-sm text-zinc-500">@{username}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-800"></div>
          </div>
        ) : followers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No followers yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {followers.map(user => (
              <div
                key={user.id}
                className="bg-white rounded-xl border border-zinc-200 p-4"
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <button
                    onClick={() => navigate(`/player/${user.username}`)}
                    className="flex-shrink-0"
                  >
                    <AvatarImage
                      src={user.profile_image_url}
                      alt={user.username}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-zinc-100"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 flex items-center justify-center ring-2 ring-zinc-100">
                        <span className="text-lg font-semibold text-zinc-500">
                          {user.username?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    </AvatarImage>
                  </button>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => navigate(`/player/${user.username}`)}
                      className="text-left w-full"
                    >
                      <p className="font-semibold text-zinc-900 truncate">{user.display_name || user.username}</p>
                      {/* Skill Badge */}
                      {(user.self_rated_skill || user.player_tier) && (
                        <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded-full border ${getSkillBadgeStyle(user.self_rated_skill || user.player_tier)}`}>
                          {user.self_rated_skill || user.player_tier}
                        </span>
                      )}
                    </button>
                  </div>
                  
                  {/* Action Buttons - Only show if not viewing own profile */}
                  {authUser && user.id !== authUser.id && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Follow/Unfollow/Requested Button */}
                      {(() => {
                        const status = followStatusMap[user.id] || { following: false, requested: false };
                        if (status.following) {
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFollow(user.id, user.username)}
                              disabled={actionLoading[user.id] === 'follow'}
                              className="h-8 px-3 text-xs border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                            >
                              {actionLoading[user.id] === 'follow' ? (
                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-3 h-3 mr-1" />
                                  Following
                                </>
                              )}
                            </Button>
                          );
                        } else if (status.requested) {
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFollow(user.id, user.username)}
                              disabled={actionLoading[user.id] === 'follow'}
                              className="h-8 px-3 text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                            >
                              {actionLoading[user.id] === 'follow' ? (
                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <Clock className="w-3 h-3 mr-1" />
                                  Requested
                                </>
                              )}
                            </Button>
                          );
                        } else {
                          return (
                            <Button
                              size="sm"
                              onClick={() => handleFollow(user.id, user.username)}
                              disabled={actionLoading[user.id] === 'follow'}
                              className="h-8 px-3 text-xs bg-orange-500 hover:bg-orange-600 text-white"
                            >
                              {actionLoading[user.id] === 'follow' ? (
                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Follow
                                </>
                              )}
                            </Button>
                          );
                        }
                      })()}
                      
                      {/* Block Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleBlock(user.id, user.username)}
                        disabled={actionLoading[user.id] === 'block'}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-600 hover:bg-red-50"
                        title="Block user"
                      >
                        {actionLoading[user.id] === 'block' ? (
                          <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Ban className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFollowers;
